import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import Header from '../components/Header';
import { 
  handleFailedAttempt, 
  isFormLocked, 
  resetAttempts,
  formatRemainingTime
} from '../utils/auth-utils';
import { useSecurity } from '../utils/SecurityContext';
import ReCAPTCHA from "react-google-recaptcha";

const Pin = ({ user, getUser }) => {
  const [pinIndex, setPinIndex] = useState(0);
  const [inputs, setInputs] = useState([]);
  const [error, setError] = useState("");
  const [pinned, setPinned] = useState(1);
  const [formLock, setFormLock] = useState({ locked: false, remainingTime: 0 });
  const [timeDisplay, setTimeDisplay] = useState("");
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const navigate = useNavigate();
  const recaptchaRef = useRef();

  const digit1 = useRef();
  const digit2 = useRef();
  const digit3 = useRef();
  const digit4 = useRef();
  
  // Utiliser le contexte de sécurité pour accéder au jeton CSRF et à l'instance axios sécurisée
  const { csrfToken, createSecureAxios } = useSecurity();
  const secureAxios = createSecureAxios();
  
  // Fonction pour mettre à jour le compteur de temps restant
  const updateRemainingTime = () => {
    const lockStatus = isFormLocked('pin');
    setFormLock(lockStatus);
    
    if (lockStatus.locked && !lockStatus.requireCaptcha) {
      setTimeDisplay(formatRemainingTime(lockStatus.remainingTime));
    } else if (lockStatus.requireCaptcha) {
      setCaptchaRequired(true);
    }
  };

  useEffect(() => {
    updateRemainingTime();
    
    // Configurer un intervalle pour mettre à jour le temps restant
    const timer = setInterval(() => {
      updateRemainingTime();
    }, 1000);
    
    return () => {
      clearInterval(timer);
    };
  }, []);

  const handlerPinInput = (ev) => {
    // Bloquer tout caractère non-numérique
    const value = ev.target.value;
    
    // Ne permettre que les chiffres
    if (!/^\d*$/.test(value)) {
      // Si ce n'est pas un chiffre, vider le champ
      ev.target.value = "";
      return;
    }
    
    // Limiter à un seul chiffre (0-9)
    if (/^\d$/.test(value)) {
      // Un seul chiffre valide, on passe au champ suivant
      setPinIndex((prevPinIndex) => {
        if (prevPinIndex < 3) {
          inputs[prevPinIndex + 1].focus(); // Déplace le focus sur le champ suivant
          return prevPinIndex + 1;
        }
        return prevPinIndex;
      });
    } else if (value.length > 1) {
      // Si plus d'un chiffre est entré, ne garder que le premier
      ev.target.value = value.charAt(0);
    } else if (value.length === 0) {
      // Si l'utilisateur efface un chiffre
      setPinIndex((prevPinIndex) => {
        if (prevPinIndex > 0) {
          // Si on n'est pas sur le premier champ, focus sur le champ précédent
          inputs[prevPinIndex - 1].focus();
          return prevPinIndex - 1;
        } else {
          // Si on est sur le premier champ, on vide tous les champs
          inputs.forEach((input) => {
            input.value = ""; // Vide chaque champ
          });
          inputs[0].focus(); // Place le focus sur le premier champ
          return 0; // Réinitialise l'index actif
        }
      });
    }
  };

  const handleInputFocus = () => {
    // Assure que le focus est sur le premier champ vide
    for (let i = 0; i < inputs.length; i++) {
      if (inputs[i].value === "") {
        inputs[i].focus();
        setPinIndex(i);
        return;
      }
    }

    // Si tous les champs sont remplis, place le focus sur le dernier champ
    inputs[inputs.length - 1].focus();
    setPinIndex(inputs.length - 1);
  };

  const handlePaste = (ev) => {
    ev.preventDefault(); // Empêche le comportement par défaut du collage
    const pastedData = ev.clipboardData.getData("text").trim(); // Récupère les données collées
  
    if (/^\d+$/.test(pastedData)) {
      // Vérifie que les données collées sont uniquement des chiffres
      const digits = pastedData.split(""); // Divise les chiffres en un tableau
      digits.forEach((digit, index) => {
        if (index < inputs.length) {
          inputs[index].value = digit; // Remplit chaque champ avec un chiffre
        }
      });
  
      // Met le focus sur le champ suivant après le dernier chiffre collé
      const nextIndex = Math.min(digits.length, inputs.length - 1);
      inputs[nextIndex].focus();
      setPinIndex(nextIndex); // Met à jour l'index actif
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("logged");
    sessionStorage.removeItem("lang");
    sessionStorage.removeItem("speed");
    localStorage.removeItem("user");
    
    // Réinitialiser les tentatives lors de la déconnexion
    resetAttempts('pin');
    resetAttempts('login');
    
    window.location.href = "/login";
  }

  const handleRecaptchaChange = (value) => {
    setCaptchaVerified(!!value);
  };

  const callPinnedApi = (edit) => {
    // Utiliser l'instance sécurisée avec CSRF
    secureAxios.post('https://connivence-salon.net/api/e-learning/users/pinned.php', new URLSearchParams({
      id: user.id, 
      edit
    }))
    .then(response => {
      // Traiter la réponse
      setPinned(parseInt(response.data.pinned));
    })
    .catch(error => {
      console.error('Erreur lors de l\'envoi de la requête:', error);
      setError("Erreur lors de la communication avec le serveur");
    });
  }

  // Version sécurisée avec CSRF de la récupération de l'utilisateur
  const secureGetUser = async (telephone) => {
    try {
      const response = await secureAxios.post('https://connivence-salon.net/api/e-learning/users/retrieve.php', 
        new URLSearchParams({
          telephone: telephone,
          lang: "fr"
        })
      );
      
      let resp = response.data.content;
      
      if (resp.length > 0) {
        localStorage.setItem("user", JSON.stringify(resp));
        return true;
      } else {
        if (localStorage.getItem("user")) {
          localStorage.removeItem("user");
        }
        return false;
      }
    } catch (error) {
      console.log("Erreur lors de la récupération de l'utilisateur:", error);
      return false;
    }
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();

    // Vérifier si le formulaire est verrouillé ou si reCAPTCHA est requis
    updateRemainingTime();
    
    if (formLock.locked && !formLock.requireCaptcha) {
      setError(`Trop de tentatives échouées. Veuillez réessayer dans ${formatRemainingTime(formLock.remainingTime)}.`);
      return;
    }
    
    // Vérifier si reCAPTCHA est requis mais non validé
    if (formLock.requireCaptcha && !captchaVerified) {
      setError("Veuillez valider le reCAPTCHA avant de continuer.");
      return;
    }

    // Vérifier que tous les champs contiennent un chiffre valide
    if (!/^\d$/.test(digit1.current.value) || 
        !/^\d$/.test(digit2.current.value) || 
        !/^\d$/.test(digit3.current.value) || 
        !/^\d$/.test(digit4.current.value)) {
      setError("Veuillez entrer un code PIN valide (4 chiffres de 0 à 9)");
      return;
    }

    let fullPin = digit1.current.value + digit2.current.value + digit3.current.value + digit4.current.value;

    setLoading(true);
    
    try {
      if (fullPin === user.pin) {
        setError("");
        // Session de 12 heures
        sessionStorage.setItem("logged", parseInt(Date.now()) + (12 * 3600000));
        sessionStorage.setItem("lang", user.lng);
        sessionStorage.setItem("speed", user.scroll_speed);
        
        // Appel à getUser avec le CSRF token via la fonction sécurisée
        if (user.telephone) {
          await secureGetUser(user.telephone);
        }
        callPinnedApi(1);
        
        // Réinitialiser les tentatives en cas de succès
        resetAttempts('pin');
        
        window.location.href = "/home";
      } else {
        setError("Code PIN incorrect !");
        
        // Enregistrer l'échec de tentative
        handleFailedAttempt('pin');
        updateRemainingTime();
      }
    } catch (err) {
      setError("Erreur lors de la vérification. Veuillez réessayer.");
      // Enregistrer l'échec de tentative
      handleFailedAttempt('pin');
      updateRemainingTime();
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    digit1.current.focus();
    setInputs([digit1.current, digit2.current, digit3.current, digit4.current]);

    if(!document.body.classList.contains("fingers"))
      document.body.classList.add("fingers");

    if(!localStorage.getItem("user")) {
      window.location.href = "/login";
    }
  }, [])

  useEffect(() => {
    const handleKeyDown = (ev) => {
      if (ev.key === "Backspace") {
        // Efface tous les champs et refocus sur le premier
        inputs.forEach((input) => {
          input.value = ""; // Vide chaque champ
        });
        inputs[0]?.focus(); // Place le focus sur le premier champ
        setPinIndex(0); // Réinitialise l'index actif
      }
    };

    // Ajoute l'écouteur d'événement
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      // Nettoie l'écouteur d'événement lors du démontage du composant
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [inputs]);

  useEffect(() => {
    if(user){
      callPinnedApi(0);
    }
  }, [user]);

  return (
    <>
      <Header />
      <main className={`pin-component ${formLock.locked && !formLock.requireCaptcha ? 'form-locked' : ''}`}>
        <h1>Votre code pin</h1>
        <button className='disconnect' onClick={handleLogout}>
          <img src='./assets/images/disconnect.svg' alt='Se déconnecter' />
          Changer de compte
        </button>
        <form onSubmit={handleSubmit}>
          <div className="code-pin">
            <input
              type="text"
              inputMode="numeric"
              id="digit1"
              ref={digit1}
              pattern="[0-9]"
              maxLength="1"
              onInput={handlerPinInput}
              onClick={handleInputFocus}
              onPaste={handlePaste}
              disabled={loading || (formLock.locked && !formLock.requireCaptcha)}
              onKeyDown={(e) => {
                if (!(e.key >= '0' && e.key <= '9') && 
                    e.key !== 'Backspace' && 
                    e.key !== 'Tab' && 
                    e.key !== 'ArrowLeft' && 
                    e.key !== 'ArrowRight' && 
                    e.key !== 'Delete') {
                  e.preventDefault();
                }
              }}
              required
            />
            <input
              type="text"
              inputMode="numeric"
              id="digit2"
              ref={digit2}
              pattern="[0-9]"
              maxLength="1"
              onInput={handlerPinInput}
              onClick={handleInputFocus}
              onPaste={handlePaste}
              disabled={loading || (formLock.locked && !formLock.requireCaptcha)}
              onKeyDown={(e) => {
                if (!(e.key >= '0' && e.key <= '9') && 
                    e.key !== 'Backspace' && 
                    e.key !== 'Tab' && 
                    e.key !== 'ArrowLeft' && 
                    e.key !== 'ArrowRight' && 
                    e.key !== 'Delete') {
                  e.preventDefault();
                }
              }}
              required
            />
            <input
              type="text"
              inputMode="numeric"
              id="digit3"
              ref={digit3}
              pattern="[0-9]"
              maxLength="1"
              onInput={handlerPinInput}
              onClick={handleInputFocus}
              onPaste={handlePaste}
              disabled={loading || (formLock.locked && !formLock.requireCaptcha)}
              onKeyDown={(e) => {
                if (!(e.key >= '0' && e.key <= '9') && 
                    e.key !== 'Backspace' && 
                    e.key !== 'Tab' && 
                    e.key !== 'ArrowLeft' && 
                    e.key !== 'ArrowRight' && 
                    e.key !== 'Delete') {
                  e.preventDefault();
                }
              }}
              required
            />
            <input
              type="text"
              inputMode="numeric"
              id="digit4"
              ref={digit4}
              pattern="[0-9]"
              maxLength="1"
              onInput={handlerPinInput}
              onClick={handleInputFocus}
              onPaste={handlePaste}
              disabled={loading || (formLock.locked && !formLock.requireCaptcha)}
              onKeyDown={(e) => {
                if (!(e.key >= '0' && e.key <= '9') && 
                    e.key !== 'Backspace' && 
                    e.key !== 'Tab' && 
                    e.key !== 'ArrowLeft' && 
                    e.key !== 'ArrowRight' && 
                    e.key !== 'Delete') {
                  e.preventDefault();
                }
              }}
              required
            />
          </div>
          
          {/* Afficher le reCAPTCHA si nécessaire */}
          {formLock.requireCaptcha && (
            <div className="recaptcha-container">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey="6LdgmxorAAAAAEe_lNxk9nQ7KdpF137SXPZzr-rs"// test dev : 6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI
                onChange={handleRecaptchaChange}
              />
            </div>
          )}
          
          {/* Afficher l'erreur ou le message de verrouillage */}
          {formLock.locked && !formLock.requireCaptcha ? (
            <p className="error-message lockout-error">
              Trop de tentatives échouées. Veuillez réessayer dans {timeDisplay}.
            </p>
          ) : error ? (
            <p className="error-message">{error}</p>
          ) : null}
          
          {pinned === 0 && (
            <p className='show-pin'><strong>Ton code pin est {user?.pin}.</strong><br />Garde-le précieusement, ce message disparaitra la prochaine fois !</p>
          )}
          
          <button disabled={loading || (formLock.locked && !formLock.requireCaptcha) || (formLock.requireCaptcha && !captchaVerified)}>
            {loading ? 'Vérification...' : 'Valider'}
            {!loading && (
              <svg width="25" height="22" viewBox="0 0 25 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14.0769 2L23 11.0011L14.0769 20M2 11.0034H23" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </button>
        </form>
      </main>
      <Footer visible={false} />
    </>
  );
};

export default Pin;